import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL } from '../config';

interface TextVersion {
  campaignId: string;
  versionId: string;
  subject: string;
  previewText?: string;
  preview?: string;
  textContent: string;
  body?: string;
  htmlContent?: string;
  disclaimer: string;
  status: string;
  approvalStatus?: 'approved' | 'pending' | 'needs_revision';
  isApproved?: boolean;
  approvedAt?: string;
  approvedBy?: string;
  approvalNote?: string;
  angle?: number;
  subjectPairs?: Array<{ subject: string; previewText: string }>;
  geo?: string;
  advertiserName?: string;
  brandName?: string;
  targetEsp?: string;
  baseVersionId?: string;
}

interface CampaignStatus {
  campaignId: string;
  advertiser: string;
  name?: string;
  geo: string;
  category: string;
  status: string;
  totalVersions: number;
  approvedCount: number;
  pendingCount: number;
  needsRevisionCount: number;
  isFullyApproved: boolean;
  percentApproved: number;
  lastAuditedAt?: string;
  lastAuditedBy?: string;
}

interface GeoStat {
  geo: string;
  totalCampaigns: number;
  fullyApprovedCampaigns: number;
  totalVersions: number;
  approvedVersions: number;
  percentApproved: number;
}

export interface AuditCheck {
  pass: boolean;
  details: string;
}

export interface AuditIssue {
  severity: 'error' | 'warning' | 'info';
  field: string;
  message: string;
  suggestion: string;
}

export interface VersionAudit {
  versionId: string;
  score: number;
  status: 'pass' | 'warning' | 'fail';
  checks: {
    tags: AuditCheck;
    landingPageAlignment: AuditCheck;
    complianceRules: AuditCheck;
    subjectLineHygiene: AuditCheck;
    disclaimer: AuditCheck;
  };
  issues: AuditIssue[];
}

export interface CampaignAuditResult {
  success: boolean;
  campaignId: string;
  campaignName: string;
  advertiser: string;
  geo: string;
  category: string;
  overallScore: number;
  overallStatus: 'pass' | 'warning' | 'fail';
  summary: string;
  landingPageOfferSummary: string;
  landingPageScraped: boolean;
  landingPageUrl?: string;
  versionAudits: VersionAudit[];
}

interface CopyAuditHubProps {
  selectedGeo: string;
  setSelectedGeo: (geo: string) => void;
  onOpenInStudio?: (campaignId: string, versionId?: string) => void;
}

const GEOS = [
  { id: 'SE', label: 'Sweden', flag: '🇸🇪' },
  { id: 'NO', label: 'Norway', flag: '🇳🇴' },
  { id: 'FI', label: 'Finland', flag: '🇫🇮' },
  { id: 'DE', label: 'Germany', flag: '🇩🇪' },
];

type CampaignCategoryType = 'loan' | 'creditcard' | 'nutra' | 'sweepstake' | 'other';

export function resolveCampaignCategory(campaign?: { category?: string; campaignId?: string; name?: string; advertiser?: string } | null): CampaignCategoryType {
  if (!campaign) return 'loan';
  const c = (campaign.category || '').toLowerCase().trim();
  const id = (campaign.campaignId || '').toLowerCase().trim();
  const n = (campaign.name || campaign.advertiser || '').toLowerCase().trim();
  const combined = `${c} ${id} ${n}`;

  if (
    c === 'nutra' ||
    combined.includes('nutra') ||
    combined.includes('melatonin') ||
    combined.includes('estrodoxin') ||
    combined.includes('collagen') ||
    combined.includes('sleepmore') ||
    combined.includes('vitaepro') ||
    combined.includes('craveless') ||
    combined.includes('collaxan') ||
    combined.includes('mory')
  ) {
    return 'nutra';
  }

  if (
    c === 'creditcard' ||
    c === 'credit card' ||
    c === 'kredittkort' ||
    c === 'kreditkarte' ||
    combined.includes('kredittkort') ||
    combined.includes('kreditkarte') ||
    combined.includes('creditcard') ||
    combined.includes('credit card') ||
    combined.includes('mastercard') ||
    combined.includes('florinkarte') ||
    combined.includes('netkredit24karte') ||
    id.endsWith('-cc-no') ||
    id.endsWith('-cc-fi') ||
    id.endsWith('-cc')
  ) {
    return 'creditcard';
  }

  if (
    c === 'sweepstake' ||
    c === 'competition' ||
    combined.includes('sweepstake') ||
    combined.includes('competition') ||
    combined.includes('konkurranse') ||
    combined.includes('tävla') ||
    combined.includes('gavekort') ||
    combined.includes('presentkort') ||
    combined.includes('supermarket') ||
    combined.includes('sparklubben')
  ) {
    return 'sweepstake';
  }

  if (
    c === 'loan' ||
    c === 'microloan' ||
    combined.includes('lån') ||
    combined.includes('lan') ||
    combined.includes('laina') ||
    combined.includes('luotto') ||
    combined.includes('kredit') ||
    combined.includes('finans') ||
    combined.includes('rahoitus')
  ) {
    return 'loan';
  }

  return 'other';
}

const CATEGORY_ANGLE_LABELS: Record<CampaignCategoryType, Record<string, Record<number, string>>> = {
  nutra: {
    NO: {
      1: 'Generisk versjon 1',
      2: 'Generisk versjon 2',
      3: 'Generisk versjon 3',
      4: 'Generisk versjon 4',
      5: 'Generisk versjon 5',
    },
    SE: {
      1: 'Generisk version 1',
      2: 'Generisk version 2',
      3: 'Generisk version 3',
      4: 'Generisk version 4',
      5: 'Generisk version 5',
    },
    FI: {
      1: 'Yleinen versio 1',
      2: 'Yleinen versio 2',
      3: 'Yleinen versio 3',
      4: 'Yleinen versio 4',
      5: 'Yleinen versio 5',
    },
    DE: {
      1: 'Generische Version 1',
      2: 'Generische Version 2',
      3: 'Generische Version 3',
      4: 'Generische Version 4',
      5: 'Generische Version 5',
    },
  },
  creditcard: {
    NO: {
      1: 'Rentefritt kjøp & Betalingsutsettelse',
      2: 'Cashback, Bonus & Rabattfordeler',
      3: '0 kr årsgebyr & Gebyrfri bruk',
      4: 'Reiseforsikring & Økonomisk trygghet',
      5: 'Fleksibel kredittramme & Reserve',
    },
    SE: {
      1: 'Räntefria köp & Betalningsanstånd',
      2: 'Cashback, Bonus & Förmåner',
      3: '0 kr årsavgift & Inga fasta avgifter',
      4: 'Reseförsäkring & Trygg buffert',
      5: 'Flexibel kreditgräns & Kontokredit',
    },
    FI: {
      1: 'Koroton maksuaika & Joustavuus',
      2: 'Cashback, Bonukset & Alennukset',
      3: '0 € vuosimaksut & Maksuton käyttö',
      4: 'Matkavakuutus & Taloudellinen turva',
      5: 'Joustava luottoraja & Puskuri',
    },
    DE: {
      1: 'Bis zu 8 Wochen zinsfreie Rückzahlung',
      2: 'Cashback & Exklusive Vorteile',
      3: 'Dauerhaft 0 € Jahresgebühr',
      4: 'Reiseversicherung & Weltweiter Schutz',
      5: 'Flexibler Verfügungsrahmen bis 4.000 €',
    },
  },
  loan: {
    NO: {
      1: 'Rentesammenligning & Konkurranseutsetting',
      2: 'Refinansiering & Samlelån',
      3: 'Digital søknad & Rask avklaring',
      4: 'Markedsoversikt & Sikkerhet',
      5: 'Enkel lånesjekk & Vilkår',
    },
    SE: {
      1: 'Räntejämförelse & Konkurrens',
      2: 'Samla lån & Sänk månadskostnad',
      3: 'Digital ansökan & Enkel översikt',
      4: 'Marknadsöversikt & Trygghet',
      5: 'Enkel lånejämförelse & Villkor',
    },
    FI: {
      1: 'Korkovertailu & Kilpailutus',
      2: 'Lainojen yhdistäminen & Kulujen karsinta',
      3: 'Digitaalinen hakemus & Helppous',
      4: 'Markkinakatsaus & Sitoumukseton',
      5: 'Selkeä lainavertailu & Ehdot',
    },
    DE: {
      1: 'Zinsvergleich & Konditionen',
      2: 'Umschuldung & Raten bündeln',
      3: 'Digitaler Online-Antrag',
      4: 'Flexibilität & Planungssicherheit',
      5: 'Einfacher Kredit-Check',
    },
  },
  sweepstake: {
    NO: {
      1: 'Delta i trekningen & Vinn premier',
      2: 'Gavekort & Hverdagsfordeler',
      3: 'Gratis & Uforpliktende deltakelse',
      4: 'Eksklusiv sjanse & Tidsbegrenset',
      5: 'Enkel registrering på nett',
    },
    SE: {
      1: 'Tävla & Vinn fina priser',
      2: 'Presentkort & Matkasse',
      3: '100% kostnadsfri registrering',
      4: 'Begränsad möjlighet & Förtur',
      5: 'Snabb digital anmälan',
    },
    FI: {
      1: 'Osallistu arvontaan & Voita',
      2: 'Lahjakortit & Arjen helpotus',
      3: 'Täysin maksuton osallistuminen',
      4: 'Rajoitettu kampanja-aika',
      5: 'Helppo rekisteröityminen',
    },
    DE: {
      1: 'Gewinnspiel-Teilnahme',
      2: 'Gutscheine & Prämien',
      3: 'Kostenlose Registrierung',
      4: 'Exklusive Gewinnchance',
      5: 'Einfache Anmeldung',
    },
  },
  other: {
    NO: {
      1: 'Generisk versjon 1',
      2: 'Generisk versjon 2',
      3: 'Generisk versjon 3',
      4: 'Generisk versjon 4',
      5: 'Generisk versjon 5',
    },
    SE: {
      1: 'Generisk version 1',
      2: 'Generisk version 2',
      3: 'Generisk version 3',
      4: 'Generisk version 4',
      5: 'Generisk version 5',
    },
    FI: {
      1: 'Yleinen versio 1',
      2: 'Yleinen versio 2',
      3: 'Yleinen versio 3',
      4: 'Yleinen versio 4',
      5: 'Yleinen versio 5',
    },
    DE: {
      1: 'Generische Version 1',
      2: 'Generische Version 2',
      3: 'Generische Version 3',
      4: 'Generische Version 4',
      5: 'Generische Version 5',
    },
  },
};

export function getAngleLabel(
  campaign: { category?: string; campaignId?: string; name?: string; advertiser?: string; geo?: string } | null | undefined,
  angle: number,
  fallbackGeo: string
): string {
  const cat = resolveCampaignCategory(campaign);
  const geo = (campaign?.geo || fallbackGeo || 'SE').toUpperCase();
  const catLabels = CATEGORY_ANGLE_LABELS[cat] || CATEGORY_ANGLE_LABELS.loan;
  const geoLabels = catLabels[geo] || catLabels.SE || {};
  return geoLabels[angle] || `Angle ${angle}`;
}

const ARCHETYPE_LABELS: Record<string, string> = {
  TA: 'TA · Full Core Layout',
  TB: 'TB · Short / Mobile-Optimized',
  TG: 'TG · Calculation & Math Focus',
  TL: 'TL · Checklist & Audit Focus',
};

export default function CopyAuditHub({ selectedGeo, setSelectedGeo, onOpenInStudio }: CopyAuditHubProps) {
  const [geoStats, setGeoStats] = useState<Record<string, GeoStat>>({});
  const [campaigns, setCampaigns] = useState<CampaignStatus[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'needs_revision'>('all');
  const [activeOnly, setActiveOnly] = useState<boolean>(true);

  // Version workspace state
  const [versions, setVersions] = useState<TextVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const fetchCounterRef = useRef<number>(0);
  const [viewMode, setViewMode] = useState<'angle' | 'version'>('angle');
  const [angleFilter, setAngleFilter] = useState<number | 'all'>('all');
  const [archetypeFilter, setArchetypeFilter] = useState<string | 'all'>('all');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [backupBanner, setBackupBanner] = useState<{ s3Key: string; downloadUrl: string; count: number } | null>(null);
  const [rejectModal, setRejectModal] = useState<{ isOpen: boolean; versionId?: string; angle?: number; note: string }>({
    isOpen: false,
    note: '',
  });

  // Live preview drawer state
  const [previewDrawer, setPreviewDrawer] = useState<{
    isOpen: boolean;
    version: TextVersion | null;
    html: string;
    loading: boolean;
    device: 'desktop' | 'mobile';
  }>({
    isOpen: false,
    version: null,
    html: '',
    loading: false,
    device: 'desktop',
  });

  // Direct version copy editor state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    version: TextVersion | null;
    subject: string;
    previewText: string;
    textContent: string;
    disclaimer: string;
    saving: boolean;
  }>({
    isOpen: false,
    version: null,
    subject: '',
    previewText: '',
    textContent: '',
    disclaimer: '',
    saving: false,
  });

  // Find & Replace Modal state
  const [findReplaceModal, setFindReplaceModal] = useState<{
    isOpen: boolean;
    findText: string;
    replaceText: string;
    matchCase: boolean;
  }>({
    isOpen: false,
    findText: '',
    replaceText: '',
    matchCase: false,
  });

  // ESP Family Filter & Multi-ESP Variation State
  const [activeEspFamily, setActiveEspFamily] = useState<'feedblitz' | 'moosend' | 'getresponse' | 'svenskalaan' | 'mailerlite' | 'brevo' | 'drip' | 'all'>('feedblitz');
  const [espStats, setEspStats] = useState<Record<string, { approved: number; total: number; pct: number }>>({});
  const [compareBaseModal, setCompareBaseModal] = useState<{
    isOpen: boolean;
    variant: TextVersion | null;
    base: TextVersion | null;
  }>({
    isOpen: false,
    variant: null,
    base: null,
  });
  const [generatingMoosend, setGeneratingMoosend] = useState(false);
  const [generatingDrip, setGeneratingDrip] = useState(false);

  // Helper to check if a version matches the active ESP family
  const matchesEspFamily = useCallback((versionId: string, family: string, itemGeo?: string): boolean => {
    const vid = (versionId || '').toLowerCase().trim();
    const g = (itemGeo || selectedGeo || '').toUpperCase();
    if (!family || family === 'all') return true;
    if (family === 'drip') return vid.endsWith('f') && g === 'SE';
    if (family === 'moosend') return vid.endsWith('c') && g === 'SE';
    if (family === 'getresponse') return (vid.endsWith('b') || vid.startsWith('tg')) && g !== 'DE';
    if (family === 'svenskalaan') return vid.endsWith('e') && g === 'SE';
    if (family === 'mailerlite') return vid.endsWith('e') && g === 'FI';
    if (family === 'brevo') return vid.endsWith('d') && g === 'FI';
    if (family === 'feedblitz') {
      return vid.endsWith('a') || (!vid.endsWith('b') && !vid.endsWith('c') && !vid.endsWith('d') && !vid.endsWith('e') && !vid.endsWith('f'));
    }
    return true;
  }, [selectedGeo]);

  // AI Compliance Audit state
  const [aiAuditLoading, setAiAuditLoading] = useState(false);
  const [aiAuditResult, setAiAuditResult] = useState<CampaignAuditResult | null>(null);
  const [aiAuditModalOpen, setAiAuditModalOpen] = useState(false);
  const [selectedAuditVersionId, setSelectedAuditVersionId] = useState<string | null>('all');

  const handleAiAudit = async () => {
    if (!activeCampaign) return;
    setAiAuditLoading(true);
    setAiAuditModalOpen(true);
    setSelectedAuditVersionId('all');
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/ai-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: activeCampaign.campaignId,
          geo: selectedGeo,
          versions: versions.length > 0 ? versions : undefined,
        }),
      });
      const data = await res.json();
      if (data.error) {
        alert(`AI Audit failed: ${data.error}`);
        setAiAuditModalOpen(false);
      } else {
        setAiAuditResult(data);
      }
    } catch (err: any) {
      alert(`AI Audit error: ${err.message || 'Unknown network error'}`);
      setAiAuditModalOpen(false);
    } finally {
      setAiAuditLoading(false);
    }
  };

  // 1. Load approval statistics with resilient fallback
  const loadApprovalStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/approval-status?geo=${selectedGeo}&espFamily=${activeEspFamily}`);
      const data = await res.json();
      if (data && !data.error && data.campaigns && data.campaigns.length > 0) {
        if (data.geos) setGeoStats(data.geos);
        if (data.espStats) setEspStats(data.espStats);
        setCampaigns(data.campaigns);
        return;
      }
      throw new Error(data?.error || 'Fallback to standard campaigns API');
    } catch (err) {
      console.warn('Approval status endpoint unavailable, falling back to /campaigns:', err);
      try {
        const res = await fetch(`${API_BASE_URL}/api/text-library/campaigns?geo=${selectedGeo}`);
        const data = await res.json();
        const rawList = data.campaigns || [];
        const mapped: CampaignStatus[] = rawList.map((c: any) => ({
          campaignId: c.campaignId,
          advertiser: c.brandName || c.advertiser || c.name || c.campaignId,
          geo: c.geo || selectedGeo,
          category: c.category || 'Loan',
          status: c.status || 'ready',
          totalVersions: c.versionCount || 5,
          approvedCount: 0,
          pendingCount: c.versionCount || 5,
          needsRevisionCount: 0,
          isFullyApproved: false,
          percentApproved: 0,
        }));
        setCampaigns(mapped);
      } catch (fbErr) {
        console.error('Failed to load campaigns fallback:', fbErr);
      }
    } finally {
      setLoadingStatus(false);
    }
  }, [selectedGeo, activeEspFamily]);

  useEffect(() => {
    setSelectedCampaignId('');
    setVersions([]);
    loadApprovalStatus();
  }, [selectedGeo, activeEspFamily, loadApprovalStatus]);

  // Ensure activeEspFamily is valid for selected market
  useEffect(() => {
    const validByGeo: Record<string, string[]> = {
      SE: ['feedblitz', 'moosend', 'getresponse', 'svenskalaan', 'all'],
      NO: ['feedblitz', 'getresponse', 'all'],
      FI: ['feedblitz', 'getresponse', 'mailerlite', 'brevo', 'all'],
      DE: ['feedblitz', 'all'],
    };
    const valid = validByGeo[selectedGeo.toUpperCase()] || ['feedblitz', 'all'];
    if (!valid.includes(activeEspFamily)) {
      setActiveEspFamily('feedblitz');
    }
  }, [selectedGeo, activeEspFamily]);

  // 2. Load versions for selected campaign
  const loadCampaignVersions = useCallback(async (cid: string) => {
    if (!cid) {
      setVersions([]);
      return;
    }
    const currentFetchId = ++fetchCounterRef.current;
    setLoadingVersions(true);
    // Immediately clear versions so stale texts from a previously selected campaign never show under cid
    setVersions([]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${cid}`);
      const data = await res.json();
      // Discard stale response if another campaign fetch has been initiated
      if (currentFetchId !== fetchCounterRef.current) return;

      const vers: TextVersion[] = data.versions || [];
      // Defensive filter: only accept versions belonging to this campaign ID
      const matchingVers = vers.filter(v => !v.campaignId || v.campaignId === cid);
      setVersions(matchingVers);

      // Compute stats dynamically for active campaign and activeEspFamily
      const familyVers = activeEspFamily === 'all' ? matchingVers : matchingVers.filter(v => matchesEspFamily(v.versionId, activeEspFamily));
      const approvedCount = familyVers.filter(v => v.approvalStatus === 'approved').length;
      const needsRevCount = familyVers.filter(v => v.approvalStatus === 'needs_revision').length;
      const total = familyVers.length;
      const pct = total > 0 ? Math.round((approvedCount / total) * 100) : 0;
      
      setCampaigns(prev => prev.map(c => {
        if (c.campaignId === cid) {
          return {
            ...c,
            totalVersions: total || c.totalVersions,
            approvedCount,
            pendingCount: total - approvedCount - needsRevCount,
            needsRevisionCount: needsRevCount,
            isFullyApproved: total > 0 && approvedCount === total,
            percentApproved: pct,
          };
        }
        return c;
      }));
    } catch (err) {
      if (currentFetchId === fetchCounterRef.current) {
        console.error(`Failed to load versions for ${cid}:`, err);
      }
    } finally {
      if (currentFetchId === fetchCounterRef.current) {
        setLoadingVersions(false);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      loadCampaignVersions(selectedCampaignId);
    } else {
      setVersions([]);
    }
  }, [selectedCampaignId, loadCampaignVersions]);

  // Generate Moosend variations for campaign on-demand
  const handleGenerateMoosendForCampaign = async (cid: string) => {
    if (!cid) return;
    setGeneratingMoosend(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/generate-esp-variant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: cid,
          targetEsp: 'moosend',
          geo: selectedGeo,
          autoApprove: true,
        }),
      });
      const data = await res.json();
      if (data.error) {
        alert(`Generation failed: ${data.error}`);
      } else {
        await loadCampaignVersions(cid);
        await loadApprovalStatus();
      }
    } catch (err: any) {
      alert(`Error generating Moosend copies: ${err.message}`);
    } finally {
      setGeneratingMoosend(false);
    }
  };

  // Generate Drip variations for campaign on-demand (TA*f)
  const handleGenerateDripForCampaign = async (cid: string) => {
    if (!cid) return;
    setGeneratingDrip(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/generate-esp-variant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: cid,
          targetEsp: 'drip',
          geo: selectedGeo,
          autoApprove: true,
        }),
      });
      const data = await res.json();
      if (data.error) {
        alert(`Generation failed: ${data.error}`);
      } else {
        await loadCampaignVersions(cid);
        await loadApprovalStatus();
      }
    } catch (err: any) {
      alert(`Error generating Drip copies: ${err.message}`);
    } finally {
      setGeneratingDrip(false);
    }
  };

  // 3. Approval Handlers with fallback
  const handleApprove = async (opts: { versionId?: string; angle?: number; archetype?: string; approveAll?: boolean }) => {
    if (!selectedCampaignId) return;
    setActionLoading(true);
    try {
      let approvedSuccessfully = false;
      try {
        const res = await fetch(`${API_BASE_URL}/api/text-library/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaignId: selectedCampaignId,
            versionId: opts.versionId,
            angle: opts.angle,
            archetype: opts.archetype,
            approveAll: opts.approveAll,
            targetEsp: activeEspFamily,
            user: 'kristian',
          }),
        });
        const data = await res.json();
        if (data && data.success) {
          approvedSuccessfully = true;
        }
      } catch (e) {
        console.warn('Approve endpoint failed, falling back to /version:', e);
      }

      if (!approvedSuccessfully) {
        // Fallback: update matching versions via /version endpoint
        const now = new Date().toISOString();
        const targets = versions.filter(v => {
          if (opts.approveAll) return matchesEspFamily(v.versionId, activeEspFamily);
          if (opts.versionId && v.versionId === opts.versionId) return true;
          if (opts.angle) {
            const m = v.versionId.match(/\d+/);
            const a = m ? parseInt(m[0], 10) : 1;
            return a === opts.angle && matchesEspFamily(v.versionId, activeEspFamily);
          }
          return false;
        });

        await Promise.all(targets.map(v => 
          fetch(`${API_BASE_URL}/api/text-library/version`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...v,
              approvalStatus: 'approved',
              approvedAt: now,
              approvedBy: 'kristian',
            }),
          })
        ));
      }

      await loadCampaignVersions(selectedCampaignId);
      await loadApprovalStatus();
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedCampaignId) return;
    setActionLoading(true);
    try {
      let rejectedSuccessfully = false;
      try {
        const res = await fetch(`${API_BASE_URL}/api/text-library/reject`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaignId: selectedCampaignId,
            versionId: rejectModal.versionId,
            angle: rejectModal.angle,
            note: rejectModal.note,
            user: 'kristian',
          }),
        });
        const data = await res.json();
        if (data && data.success) {
          rejectedSuccessfully = true;
        }
      } catch (e) {
        console.warn('Reject endpoint failed, falling back to /version:', e);
      }

      if (!rejectedSuccessfully) {
        const targets = versions.filter(v => {
          if (rejectModal.versionId && v.versionId === rejectModal.versionId) return true;
          if (rejectModal.angle) {
            const m = v.versionId.match(/\d+/);
            const a = m ? parseInt(m[0], 10) : 1;
            return a === rejectModal.angle;
          }
          return false;
        });

        await Promise.all(targets.map(v => 
          fetch(`${API_BASE_URL}/api/text-library/version`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...v,
              approvalStatus: 'needs_revision',
              approvalNote: rejectModal.note,
            }),
          })
        ));
      }

      setRejectModal({ isOpen: false, note: '' });
      await loadCampaignVersions(selectedCampaignId);
      await loadApprovalStatus();
    } catch (err) {
      console.error('Reject failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Create S3 Backup Snapshot
  const handleCreateBackup = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/backup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geo: selectedGeo }),
      });
      const data = await res.json();
      if (data.success) {
        setBackupBanner({
          s3Key: data.s3Key,
          downloadUrl: data.downloadUrl,
          count: data.count,
        });
      }
    } catch (err) {
      console.error('Backup failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // 5. Direct Local JSON Export
  const handleExportJson = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/approved-export?geo=${selectedGeo}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approved_texts_${selectedGeo}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // 6. Live ESP Preview in Drawer
  const handleOpenPreview = async (ver: TextVersion) => {
    let defaultEsp = selectedGeo === 'SE' ? 'fb_se' : selectedGeo === 'NO' ? 'gr_no' : selectedGeo === 'FI' ? 'ml_fi' : 'fb_de';
    if ((ver.versionId || '').toLowerCase().endsWith('c') || activeEspFamily === 'moosend') {
      defaultEsp = 'ms_se';
    } else if ((ver.versionId || '').toLowerCase().endsWith('b') || activeEspFamily === 'getresponse') {
      defaultEsp = selectedGeo === 'NO' ? 'gr_no' : selectedGeo === 'FI' ? 'gr_fi' : 'gr_se';
    } else if ((ver.versionId || '').toLowerCase().endsWith('e') || activeEspFamily === 'svenskalaan' || activeEspFamily === 'mailerlite') {
      defaultEsp = selectedGeo === 'SE' ? 'gr_se2' : 'ml_fi';
    } else if ((ver.versionId || '').toLowerCase().endsWith('d') || activeEspFamily === 'brevo') {
      defaultEsp = 'br_fi';
    }

    setPreviewDrawer({
      isOpen: true,
      version: ver,
      html: '',
      loading: true,
      device: 'desktop',
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/render-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          textContent: ver.textContent || ver.body || '',
          previewText: ver.previewText || ver.preview || '',
          disclaimer: ver.disclaimer || '',
          espId: defaultEsp,
          geo: selectedGeo,
          campaignId: ver.campaignId,
          versionId: ver.versionId,
        }),
      });
      const data = await res.json();
      setPreviewDrawer(prev => ({
        ...prev,
        html: data.html || `<div style="padding:20px;color:red;">Error: ${data.error || 'Failed to render'}</div>`,
        loading: false,
      }));
    } catch (err: any) {
      setPreviewDrawer(prev => ({
        ...prev,
        html: `<div style="padding:20px;color:red;">Render error: ${err.message}</div>`,
        loading: false,
      }));
    }
  };

  // 7. Direct Version Copy Editor
  const handleOpenEdit = (ver: TextVersion) => {
    setEditModal({
      isOpen: true,
      version: ver,
      subject: ver.subject || '',
      previewText: ver.previewText || ver.preview || '',
      textContent: ver.textContent || ver.body || '',
      disclaimer: ver.disclaimer || '',
      saving: false,
    });
  };

  const handleSaveEdit = async (andApprove: boolean = false) => {
    if (!editModal.version) return;
    setEditModal(prev => ({ ...prev, saving: true }));
    try {
      const updated: TextVersion = {
        ...editModal.version,
        subject: editModal.subject,
        previewText: editModal.previewText,
        preview: editModal.previewText,
        textContent: editModal.textContent,
        body: editModal.textContent,
        disclaimer: editModal.disclaimer,
        status: 'ready',
      };

      // 1. Save updated content to TextVersions table
      const res = await fetch(`${API_BASE_URL}/api/text-library/version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // 2. If "Save & Approve" requested, also call approve endpoint
      if (andApprove) {
        await fetch(`${API_BASE_URL}/api/text-library/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaignId: updated.campaignId,
            versionId: updated.versionId,
            user: 'kristian',
          }),
        });
      }

      setEditModal(prev => ({ ...prev, isOpen: false, version: null }));
      if (selectedCampaignId) {
        await loadCampaignVersions(selectedCampaignId);
      }
      await loadApprovalStatus();
    } catch (err: any) {
      console.error('Failed to save version edit:', err);
      alert(`Save failed: ${err.message || err}`);
    } finally {
      setEditModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleQuickReplaceTerms = () => {
    const replaceIn = (txt: string) => {
      return txt
        .replace(/\bla banker\b/gi, 'la långivere')
        .replace(/\bbanker\b/gi, 'långivere')
        .replace(/\bbankene\b/gi, 'långiverne')
        .replace(/\bbankers\b/gi, 'långiveres');
    };
    setEditModal(prev => ({
      ...prev,
      subject: replaceIn(prev.subject),
      previewText: replaceIn(prev.previewText),
      textContent: replaceIn(prev.textContent),
    }));
  };

  const isCampaignActive = (status?: string) => {
    const s = (status || 'ready').toLowerCase();
    return s === 'active' || s === 'ready';
  };

  // Filtered campaigns for sidebar
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if ((c.geo || '').toUpperCase() !== selectedGeo.toUpperCase()) return false;
      if (activeOnly && !isCampaignActive(c.status)) return false;
      const matchesSearch = !searchQuery || c.advertiser.toLowerCase().includes(searchQuery.toLowerCase()) || c.campaignId.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (statusFilter === 'approved') return c.isFullyApproved;
      if (statusFilter === 'pending') return !c.isFullyApproved && c.approvedCount === 0;
      if (statusFilter === 'needs_revision') return c.needsRevisionCount > 0;
      return true;
    });
  }, [campaigns, selectedGeo, searchQuery, statusFilter, activeOnly]);

  const activeCampaign = useMemo(() => {
    return campaigns.find(c => c.campaignId === selectedCampaignId);
  }, [campaigns, selectedCampaignId]);

  // Keep selectedCampaignId valid when filteredCampaigns changes
  useEffect(() => {
    if (filteredCampaigns.length > 0) {
      const exists = filteredCampaigns.some(c => c.campaignId === selectedCampaignId);
      if (!exists) {
        setSelectedCampaignId(filteredCampaigns[0].campaignId);
      }
    } else {
      setSelectedCampaignId('');
    }
  }, [filteredCampaigns, selectedCampaignId]);

  // Group versions by Angle (1 to 5) and Archetype (TA, TB, TG, TL)
  const angleGroups = useMemo(() => {
    const groups: Record<string, { angle: number; archetype: string; items: TextVersion[]; representative: TextVersion; baseVersion?: TextVersion }> = {};
    
    // Filter to selected campaign and active ESP family
    const filteredByEsp = versions
      .filter(v => !v.campaignId || v.campaignId === selectedCampaignId)
      .filter(v => matchesEspFamily(v.versionId, activeEspFamily));

    filteredByEsp.forEach(v => {
      const vid = v.versionId || '';
      const arch = vid.slice(0, 2).toUpperCase();
      const angleMatch = vid.match(/^[A-Za-z]{2}(\d+)/);
      const ang = v.angle || (angleMatch ? parseInt(angleMatch[1], 10) : 1);

      if (angleFilter !== 'all' && ang !== angleFilter) return;
      if (archetypeFilter !== 'all' && arch !== archetypeFilter) return;

      const key = `${ang}_${arch}`;
      if (!groups[key]) {
        // Find matching base version across all versions of this campaign
        const baseNumMatch = vid.match(/\d+/);
        const baseNum = baseNumMatch ? baseNumMatch[0] : '1';
        const baseVer = versions.find(bv => {
          if (bv.campaignId && bv.campaignId !== v.campaignId) return false;
          const bvid = (bv.versionId || '').toLowerCase();
          return bvid === `ta${baseNum}` || bvid === `ta${baseNum}a`;
        });

        groups[key] = {
          angle: ang,
          archetype: arch,
          items: [],
          representative: v,
          baseVersion: baseVer,
        };
      }
      groups[key].items.push(v);
      if (activeEspFamily === 'moosend') {
        if (vid.endsWith('c')) {
          groups[key].representative = v;
        }
      } else if (vid.endsWith('a') || vid.length === 3) {
        groups[key].representative = v;
      }
    });
    return Object.values(groups).sort((a, b) => a.angle - b.angle || a.archetype.localeCompare(b.archetype));
  }, [versions, selectedCampaignId, angleFilter, archetypeFilter, activeEspFamily, matchesEspFamily]);

  // Current GEO summary (reflects active-only filter)
  const currentGeoStat = useMemo(() => {
    const relevant = campaigns.filter(c => (c.geo || '').toUpperCase() === selectedGeo.toUpperCase() && (!activeOnly || isCampaignActive(c.status)));
    const totalCampaigns = relevant.length;
    const fullyApprovedCampaigns = relevant.filter(c => c.isFullyApproved).length;
    const totalVersions = relevant.reduce((sum, c) => sum + (c.totalVersions || 0), 0);
    const approvedVersions = relevant.reduce((sum, c) => sum + (c.approvedCount || 0), 0);
    const percentApproved = totalVersions > 0 ? Math.round((approvedVersions / totalVersions) * 100) : 0;
    return {
      geo: selectedGeo,
      totalCampaigns,
      fullyApprovedCampaigns,
      totalVersions,
      approvedVersions,
      percentApproved,
    };
  }, [campaigns, selectedGeo, activeOnly]);

  // Dynamic active tab percentages for all 4 GEOs
  const geoPercentages = useMemo(() => {
    const map: Record<string, number> = {};
    GEOS.forEach(g => {
      const geoCamps = campaigns.filter(c => (c.geo || '').toUpperCase() === g.id && (!activeOnly || isCampaignActive(c.status)));
      const tot = geoCamps.reduce((sum, c) => sum + (c.totalVersions || 0), 0);
      const app = geoCamps.reduce((sum, c) => sum + (c.approvedCount || 0), 0);
      map[g.id] = tot > 0 ? Math.round((app / tot) * 100) : (geoStats[g.id]?.percentApproved || 0);
    });
    return map;
  }, [campaigns, geoStats, activeOnly]);

  return (
    <div className="space-y-6">
      {/* Top Header Bar: GEO Switcher & Global Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* GEO Tabs */}
          <div className="flex items-center gap-2">
            {GEOS.map(g => {
              const pct = geoPercentages[g.id] ?? (geoStats[g.id]?.percentApproved || 0);
              const isSelected = selectedGeo === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setSelectedGeo(g.id)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-base">{g.flag}</span>
                  <span>{g.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-indigo-700 text-indigo-100' : 'bg-gray-300 text-gray-800'}`}>
                    {pct}%
                  </span>
                </button>
              );
            })}
          </div>

          {/* Action Tools: Backup & Export */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateBackup}
              disabled={actionLoading}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
              title="Upload timestamped immutable snapshot of approved copy to S3"
            >
              <span>💾</span>
              <span>Create S3 Backup</span>
            </button>
            <button
              onClick={handleExportJson}
              className="px-3.5 py-2 bg-gray-800 hover:bg-gray-900 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
              title="Download local JSON of approved versions"
            >
              <span>📥</span>
              <span>Export JSON</span>
            </button>
            <button
              onClick={loadApprovalStatus}
              className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg text-sm"
              title="Refresh status"
            >
              🔄
            </button>
          </div>
        </div>

        {/* ESP Family Tabs */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">ESP Target:</span>
            {[
              { id: 'feedblitz' as const, label: 'FeedBlitz (Base)', icon: '⚡', key: 'feedblitz' },
              ...(selectedGeo === 'SE' ? [
                { id: 'moosend' as const, label: 'Moosend', icon: '📬', key: 'moosend' },
                { id: 'svenskalaan' as const, label: 'Svenska Lån', icon: '🇸🇪', key: 'svenskalaan' },
                { id: 'drip' as const, label: 'Drip (Enkelt Lån)', icon: '💧', key: 'drip' },
              ] : []),
              ...(selectedGeo !== 'DE' ? [{ id: 'getresponse' as const, label: 'GetResponse', icon: '📨', key: 'getresponse' }] : []),
              ...(selectedGeo === 'FI' ? [
                { id: 'mailerlite' as const, label: 'MailerLite', icon: '💌', key: 'mailerlite' },
                { id: 'brevo' as const, label: 'Brevo FI', icon: '📮', key: 'brevo' },
              ] : []),
              { id: 'all' as const, label: 'All ESPs', icon: '🌐', key: 'all' },
            ].map(esp => {
              const isSelected = activeEspFamily === esp.id;
              const stat = espStats[esp.key];
              const pctText = stat && stat.total > 0 ? `${stat.pct}%` : null;

              return (
                <button
                  key={esp.id}
                  onClick={() => setActiveEspFamily(esp.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    isSelected
                      ? 'bg-slate-900 text-white shadow-sm ring-2 ring-slate-400'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{esp.icon}</span>
                  <span>{esp.label}</span>
                  {pctText && (
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
                      isSelected ? 'bg-slate-700 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {pctText}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="text-xs">
            {activeEspFamily === 'feedblitz' ? (
              <span className="text-amber-800 font-semibold bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200">
                ⚡ Viewing Approved Golden Master Copies (FeedBlitz baseline)
              </span>
            ) : activeEspFamily === 'moosend' ? (
              <span className="text-indigo-800 font-semibold bg-indigo-50 px-2.5 py-1 rounded-md border border-indigo-200">
                📬 Viewing Moosend Varied Editions (TA1c–TA5c for swiftkredit.se)
              </span>
            ) : activeEspFamily === 'svenskalaan' ? (
              <span className="text-emerald-800 font-semibold bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200">
                🇸🇪 Viewing Svenska Lån Editions (TA1e–TA5e for svenskalan.se)
              </span>
            ) : activeEspFamily === 'mailerlite' ? (
              <span className="text-pink-800 font-semibold bg-pink-50 px-2.5 py-1 rounded-md border border-pink-200">
                💌 Viewing MailerLite Editions (TA1e–TA5e for tarjouksesi.com)
              </span>
            ) : activeEspFamily === 'brevo' ? (
              <span className="text-teal-800 font-semibold bg-teal-50 px-2.5 py-1 rounded-md border border-teal-200">
                📮 Viewing Brevo Generic Editions (TA1d–TA5d for Finland)
              </span>
            ) : activeEspFamily === 'getresponse' ? (
              <span className="text-blue-800 font-semibold bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200">
                📨 Viewing GetResponse Editions (TA1b–TA5b)
              </span>
            ) : (
              <span className="text-gray-700 font-semibold bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200">
                🌐 Viewing All Editions Combined
              </span>
            )}
          </div>
        </div>

        {/* GEO Progress Bar */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">{currentGeoStat.fullyApprovedCampaigns}</span> of{' '}
              <span className="font-semibold text-gray-900">{currentGeoStat.totalCampaigns}</span> campaigns approved
            </div>
            <div>•</div>
            <div>
              <span className="font-semibold text-gray-900">{currentGeoStat.approvedVersions.toLocaleString()}</span> of{' '}
              <span className="font-semibold text-gray-900">{currentGeoStat.totalVersions.toLocaleString()}</span> versions approved
            </div>
          </div>
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-emerald-500 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${currentGeoStat.percentApproved}%` }}
              />
            </div>
            <span className="text-xs font-bold text-gray-700 whitespace-nowrap">
              {currentGeoStat.percentApproved}%
            </span>
          </div>
        </div>
      </div>

      {/* S3 Backup Notification Banner */}
      {backupBanner && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-center gap-3 text-sm text-emerald-800">
            <span className="text-xl">✅</span>
            <div>
              <div className="font-bold">S3 Backup Snapshot Created Successfully!</div>
              <div className="text-xs text-emerald-700">
                Backed up {backupBanner.count} approved versions to: <code className="font-mono">{backupBanner.s3Key}</code>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={backupBanner.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm"
            >
              Download Snapshot
            </a>
            <button
              onClick={() => setBackupBanner(null)}
              className="text-emerald-600 hover:text-emerald-800 p-1 text-sm font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Main 2-Column Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column (4 cols): Campaign Selector */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-800 text-sm">Campaigns ({filteredCampaigns.length})</h3>
                <button
                  type="button"
                  onClick={() => setActiveOnly(prev => !prev)}
                  className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border transition-all flex items-center gap-1.5 ${
                    activeOnly
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                  }`}
                  title={activeOnly ? "Showing active/ready campaigns only. Click to show all including inactive" : "Showing all campaigns. Click to filter active only"}
                >
                  <span className={`w-2 h-2 rounded-full ${activeOnly ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                  {activeOnly ? 'Active Only' : 'Show All'}
                </button>
              </div>
              <span className="text-xs text-gray-400">Select to audit</span>
            </div>

            {/* Search Input */}
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Filter by name or ID..."
              className="w-full text-xs rounded-lg px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 focus:bg-white"
            />

            {/* Status Filter Chips */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1 text-xs">
              {(['all', 'pending', 'approved', 'needs_revision'] as const).map(st => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  className={`px-2.5 py-1 rounded-md capitalize font-medium transition-colors ${
                    statusFilter === st
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {st.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* Campaign List */}
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
              {loadingStatus ? (
                <div className="text-center py-8 text-xs text-gray-400">Loading campaigns...</div>
              ) : filteredCampaigns.length === 0 ? (
                <div className="text-center py-8 text-xs text-gray-400">No campaigns match filters</div>
              ) : (
                filteredCampaigns.map(c => {
                  const isSelected = selectedCampaignId === c.campaignId;
                  const isComplete = c.isFullyApproved;
                  return (
                    <button
                      key={c.campaignId}
                      onClick={() => setSelectedCampaignId(c.campaignId)}
                      className={`w-full text-left p-3 rounded-xl border transition-all text-xs flex items-center justify-between gap-2 ${
                        isSelected
                          ? 'bg-indigo-50 border-indigo-400 shadow-sm'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-gray-900 truncate">{c.advertiser}</span>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${
                              (c.status || '').toLowerCase() === 'active'
                                ? 'bg-emerald-100 text-emerald-800'
                                : (c.status || '').toLowerCase() === 'ready'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {c.status || 'ready'}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-400 truncate">
                          {c.campaignId} · {c.category}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            isComplete
                              ? 'bg-emerald-100 text-emerald-800'
                              : c.approvedCount > 0
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {c.approvedCount} / {c.totalVersions}
                        </span>
                        {c.needsRevisionCount > 0 && (
                          <span className="ml-1 inline-block px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                            ⚠️ {c.needsRevisionCount}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Column (8 cols): Audit Workspace */}
        <div className="lg:col-span-8 space-y-4">
          {activeCampaign ? (
            <>
              {/* Campaign Header & Global Approve */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <h2 className="text-xl font-bold text-gray-900">{activeCampaign.advertiser}</h2>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        {activeCampaign.geo} · {activeCampaign.category}
                      </span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                          (activeCampaign.status || '').toLowerCase() === 'active'
                            ? 'bg-emerald-100 text-emerald-800'
                            : (activeCampaign.status || '').toLowerCase() === 'ready'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {activeCampaign.status || 'ready'}
                      </span>
                      {activeCampaign.isFullyApproved && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                          ✓ Fully Approved
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                      <span>ID: <code className="font-mono">{activeCampaign.campaignId}</code></span>
                      <span>•</span>
                      <span>
                        Status: <strong>{activeCampaign.approvedCount} of {activeCampaign.totalVersions} approved</strong> ({activeCampaign.percentApproved}%)
                      </span>
                      {activeCampaign.lastAuditedAt && (
                        <>
                          <span>•</span>
                          <span>Last audited: {new Date(activeCampaign.lastAuditedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Batch Actions */}
                  <div className="flex items-center gap-2">
                    {activeEspFamily === 'moosend' && (
                      <button
                        onClick={() => handleGenerateMoosendForCampaign(activeCampaign.campaignId)}
                        disabled={generatingMoosend || actionLoading}
                        className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-all disabled:opacity-50"
                        title="Generate or re-generate Moosend variants from FeedBlitz base"
                      >
                        <span>⚡</span>
                        <span>{generatingMoosend ? 'Generating...' : 'Regenerate Moosend (TA*c)'}</span>
                      </button>
                    )}
                    {activeEspFamily === 'drip' && (
                      <button
                        onClick={() => handleGenerateDripForCampaign(activeCampaign.campaignId)}
                        disabled={generatingDrip || actionLoading}
                        className="px-3.5 py-2 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-all disabled:opacity-50"
                        title="Generate or re-generate Drip variants (TA*f) from FeedBlitz base"
                      >
                        <span>⚡</span>
                        <span>{generatingDrip ? 'Generating...' : 'Regenerate Drip (TA*f)'}</span>
                      </button>
                    )}
                    <button
                      onClick={handleAiAudit}
                      disabled={aiAuditLoading || actionLoading}
                      className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-all disabled:opacity-50"
                      title="Run automated compliance, tag, and landing page audit"
                    >
                      <span>🤖</span>
                      <span>{aiAuditLoading ? 'Auditing...' : `AI Audit (${activeEspFamily === 'moosend' ? 'Moosend' : 'Campaign'})`}</span>
                    </button>
                    <button
                      onClick={() => handleApprove({ approveAll: true })}
                      disabled={actionLoading || (activeCampaign.totalVersions > 0 && activeCampaign.approvedCount === activeCampaign.totalVersions)}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                      <span>✓</span>
                      <span>
                        Approve {activeEspFamily === 'moosend' ? 'Moosend Copies' : activeEspFamily === 'feedblitz' ? 'Base Copies' : 'Entire Campaign'} ({activeCampaign.totalVersions})
                      </span>
                    </button>
                    {onOpenInStudio && (
                      <button
                        onClick={() => onOpenInStudio(activeCampaign.campaignId)}
                        className="px-3 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                      >
                        Studio Editor
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Toolbar for Versions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* View Mode Toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                      <button
                        onClick={() => setViewMode('angle')}
                        className={`px-3 py-1 rounded-md font-semibold transition-all ${
                          viewMode === 'angle' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        By Angle & Archetype
                      </button>
                      <button
                        onClick={() => setViewMode('version')}
                        className={`px-3 py-1 rounded-md font-semibold transition-all ${
                          viewMode === 'version' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        All Individual Versions ({versions.filter(v => !v.campaignId || v.campaignId === selectedCampaignId).length})
                      </button>
                    </div>

                    {/* Angle Filter Dropdown */}
                    <select
                      value={angleFilter}
                      onChange={e => setAngleFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
                      className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs text-gray-700"
                    >
                      <option value="all">All Angles (1–5)</option>
                      {[1, 2, 3, 4, 5].map(a => (
                        <option key={a} value={a}>
                          Angle {a}: {getAngleLabel(activeCampaign, a, selectedGeo)}
                        </option>
                      ))}
                    </select>

                    {/* Archetype Filter Dropdown */}
                    <select
                      value={archetypeFilter}
                      onChange={e => setArchetypeFilter(e.target.value)}
                      className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs text-gray-700"
                    >
                      <option value="all">All Archetypes</option>
                      <option value="TA">TA · Core</option>
                      <option value="TB">TB · Short</option>
                      <option value="TG">TG · Math/Calc</option>
                      <option value="TL">TL · Checklist</option>
                    </select>
                  </div>

                  <div className="text-gray-400">
                    {selectedGeo === 'DE' ? (
                      <span>Review and approve each of the <strong>5 distinct angles</strong> for FeedBlitz.</span>
                    ) : (
                      <span>Click <strong>Approve Angle</strong> to vet all variants simultaneously.</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Version Cards Feed */}
              {loadingVersions ? (
                <div className="bg-white rounded-xl p-12 text-center text-sm text-gray-400 border border-gray-200">
                  Loading text versions...
                </div>
              ) : viewMode === 'angle' ? (
                /* Angle Group View */
                angleGroups.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center space-y-3 shadow-sm">
                    <div className="text-3xl">📬</div>
                    <div className="text-base font-bold text-gray-900">
                      No {activeEspFamily === 'moosend' ? 'Moosend' : activeEspFamily === 'drip' ? 'Drip (Enkelt Lån)' : activeEspFamily === 'getresponse' ? 'GetResponse' : ''} versions found for this campaign
                    </div>
                    <p className="text-xs text-gray-500 max-w-md mx-auto">
                      {activeEspFamily === 'moosend'
                        ? 'Generate high-converting Moosend variations (TA1c–TA5c) with varied wording from the approved FeedBlitz baseline.'
                        : activeEspFamily === 'drip'
                        ? 'Generate dedicated Drip variations (TA1f–TA5f) for Enkelt Lån with "same but different" wording from the approved FeedBlitz baseline.'
                        : 'Switch to FeedBlitz to view base copy or generate variations.'}
                    </p>
                    {activeEspFamily === 'moosend' && (
                      <button
                        onClick={() => handleGenerateMoosendForCampaign(selectedCampaignId)}
                        disabled={generatingMoosend}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-sm transition-all"
                      >
                        {generatingMoosend ? 'Generating Moosend Copies...' : '⚡ Generate Moosend Copies from Base'}
                      </button>
                    )}
                    {activeEspFamily === 'drip' && (
                      <button
                        onClick={() => handleGenerateDripForCampaign(selectedCampaignId)}
                        disabled={generatingDrip}
                        className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-lg shadow-sm transition-all"
                      >
                        {generatingDrip ? 'Generating Drip Copies...' : '⚡ Generate Drip Copies from Base (TA*f)'}
                      </button>
                    )}
                  </div>
                ) : (
                <div className="space-y-4">
                  {angleGroups.map(group => {
                    const rep = group.representative;
                    const allApproved = group.items.every(i => i.approvalStatus === 'approved');
                    const anyNeedsRevision = group.items.some(i => i.approvalStatus === 'needs_revision');
                    const approvedCount = group.items.filter(i => i.approvalStatus === 'approved').length;

                    return (
                      <div
                        key={`${group.angle}_${group.archetype}`}
                        className={`bg-white rounded-xl shadow-sm border p-5 transition-all ${
                          allApproved
                            ? 'border-emerald-300 bg-emerald-50/10'
                            : anyNeedsRevision
                            ? 'border-red-300 bg-red-50/10'
                            : 'border-gray-200'
                        }`}
                      >
                        {/* Card Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-gray-100">
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-indigo-100 text-indigo-800">
                              Angle {group.angle}
                            </span>
                            <span className="font-bold text-gray-900 text-sm">
                              {getAngleLabel(activeCampaign, group.angle, selectedGeo)}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                              {ARCHETYPE_LABELS[group.archetype] || group.archetype}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${
                                allApproved
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : anyNeedsRevision
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {allApproved
                                ? '✓ Approved'
                                : anyNeedsRevision
                                ? '⚠️ Needs Revision'
                                : `${approvedCount} / ${group.items.length} Approved`}
                            </span>
                            {rep.approvedAt && (
                              <span className="text-[11px] text-gray-400">
                                {new Date(rep.approvedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Subject Lines Rotations */}
                        <div className="mt-3 space-y-1.5">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                            Subject Lines (Rotated):
                          </div>
                          {rep.subjectPairs && rep.subjectPairs.length > 0 ? (
                            rep.subjectPairs.map((pair, idx) => (
                              <div key={idx} className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100 space-y-0.5">
                                <div className="font-semibold text-gray-800 flex items-center gap-2">
                                  <span className="text-[10px] px-1.5 py-0.2 bg-gray-200 text-gray-700 rounded font-bold">
                                    Rotation {idx + 1}
                                  </span>
                                  {pair.subject}
                                </div>
                                <div className="text-[11px] text-gray-500 italic">
                                  Preview: {pair.previewText || '—'}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                              <span className="font-semibold text-gray-800">{rep.subject}</span>
                              <div className="text-[11px] text-gray-500 italic mt-0.5">
                                Preview: {rep.previewText || rep.preview || '—'}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Body Preview */}
                        <div className="mt-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                            Email Body Text:
                          </div>
                          <div className="text-xs text-gray-700 bg-white border border-gray-100 rounded-lg p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">
                            {rep.textContent || rep.body}
                          </div>
                        </div>

                        {/* Disclaimer Box */}
                        {rep.disclaimer && (
                          <div className="mt-2 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2.5 italic">
                            <strong className="not-italic text-gray-700">Legal Representative Example:</strong> {rep.disclaimer}
                          </div>
                        )}

                        {/* Card Action Buttons */}
                        <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs text-gray-400">
                            Applies to: {group.items.map(i => i.versionId).join(', ')}
                          </div>

                          <div className="flex items-center gap-2">
                            {group.baseVersion && group.baseVersion.versionId !== rep.versionId && (
                              <button
                                onClick={() => setCompareBaseModal({ isOpen: true, variant: rep, base: group.baseVersion! })}
                                className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 border border-purple-200"
                                title="Compare this variant side-by-side with approved FeedBlitz base copy"
                              >
                                <span>🔍</span>
                                <span>Compare with Base</span>
                              </button>
                            )}

                            <button
                              onClick={() => handleOpenPreview(rep)}
                              className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1"
                            >
                              <span>👁️</span>
                              <span>Live Preview</span>
                            </button>

                            <button
                              onClick={() => handleOpenEdit(rep)}
                              className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 border border-amber-200"
                              title="Directly edit subject, body, or disclaimer"
                            >
                              <span>✏️</span>
                              <span>Edit Copy</span>
                            </button>

                            <button
                              onClick={() =>
                                setRejectModal({
                                  isOpen: true,
                                  angle: group.angle,
                                  note: '',
                                })
                              }
                              className="px-3 py-1.5 border border-red-200 hover:bg-red-50 text-red-700 text-xs font-semibold rounded-lg transition-colors"
                            >
                              Flag
                            </button>

                            <button
                              onClick={() =>
                                handleApprove({
                                  angle: group.angle,
                                  archetype: group.archetype,
                                })
                              }
                              disabled={actionLoading || allApproved}
                              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg shadow-sm transition-colors flex items-center gap-1 ${
                                allApproved
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              }`}
                            >
                              <span>✓</span>
                              <span>{allApproved ? 'Approved' : group.items.length === 1 ? 'Approve Version' : `Approve Angle (${group.items.length} Variants)`}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )) : (
                /* Individual Version View */
                <div className="space-y-3">
                  {versions
                    .filter(v => !v.campaignId || v.campaignId === selectedCampaignId)
                    .filter(v => {
                      if (statusFilter === 'approved') return v.approvalStatus === 'approved';
                      if (statusFilter === 'pending') return !v.approvalStatus || v.approvalStatus === 'pending';
                      if (statusFilter === 'needs_revision') return v.approvalStatus === 'needs_revision';
                      return true;
                    })
                    .map(v => (
                      <div
                        key={v.versionId}
                        className={`bg-white rounded-xl shadow-sm border p-4 text-xs space-y-2 ${
                          v.approvalStatus === 'approved'
                            ? 'border-emerald-300'
                            : v.approvalStatus === 'needs_revision'
                            ? 'border-red-300'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 text-sm">{v.versionId}</span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                v.approvalStatus === 'approved'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : v.approvalStatus === 'needs_revision'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {v.approvalStatus || 'pending'}
                            </span>
                            {v.approvedAt && (
                              <span className="text-gray-400 text-[10px]">
                                Approved by {v.approvedBy || 'user'} on {new Date(v.approvedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleOpenPreview(v)}
                              className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded font-semibold"
                            >
                              Preview
                            </button>
                            <button
                              onClick={() => handleOpenEdit(v)}
                              className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded font-semibold border border-amber-200 flex items-center gap-1"
                              title="Directly edit subject, body, or disclaimer"
                            >
                              <span>✏️</span>
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => setRejectModal({ isOpen: true, versionId: v.versionId, note: '' })}
                              className="px-2.5 py-1 border border-red-200 hover:bg-red-50 text-red-700 rounded font-semibold"
                            >
                              Flag
                            </button>
                            <button
                              onClick={() => handleApprove({ versionId: v.versionId })}
                              disabled={v.approvalStatus === 'approved'}
                              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded font-bold"
                            >
                              {v.approvalStatus === 'approved' ? '✓ Approved' : 'Approve'}
                            </button>
                          </div>
                        </div>

                        <div>
                          <span className="font-semibold text-gray-700">Subject: </span>
                          <span className="text-gray-900">{v.subject}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Preview: </span>
                          <span className="text-gray-600 italic">{v.previewText || v.preview || '—'}</span>
                        </div>
                        <div className="text-gray-700 bg-gray-50 rounded p-2 max-h-24 overflow-y-auto whitespace-pre-wrap">
                          {v.textContent || v.body}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-sm text-gray-400">
              Select a campaign on the left to begin audit and approval.
            </div>
          )}
        </div>
      </div>

      {/* Flag / Reject Reason Modal */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4 animate-scale-up">
            <h3 className="font-bold text-gray-900 text-base">Flag Copy for Revision</h3>
            <p className="text-xs text-gray-500">
              Explain why this text needs revision (e.g., phrasing issue, outdated amount, or missing disclaimer).
            </p>
            <textarea
              value={rejectModal.note}
              onChange={e => setRejectModal(prev => ({ ...prev, note: e.target.value }))}
              placeholder="e.g. Tone too aggressive, please revise subject line..."
              className="w-full h-24 text-xs border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setRejectModal({ isOpen: false, note: '' })}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg shadow-sm"
              >
                Flag as Needs Revision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live ESP Preview Drawer */}
      {previewDrawer.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end animate-fade-in">
          <div className="bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col animate-slide-left">
            {/* Drawer Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Live ESP Template Preview</h3>
                <p className="text-xs text-gray-400">
                  {previewDrawer.version?.campaignId} · {previewDrawer.version?.versionId}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200 text-xs">
                  <button
                    onClick={() => setPreviewDrawer(prev => ({ ...prev, device: 'desktop' }))}
                    className={`px-2.5 py-1 rounded font-semibold ${
                      previewDrawer.device === 'desktop' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    🖥️ Desktop
                  </button>
                  <button
                    onClick={() => setPreviewDrawer(prev => ({ ...prev, device: 'mobile' }))}
                    className={`px-2.5 py-1 rounded font-semibold ${
                      previewDrawer.device === 'mobile' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    📱 Mobile
                  </button>
                </div>
                <button
                  onClick={() => setPreviewDrawer(prev => ({ ...prev, isOpen: false }))}
                  className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Email Meta Bar */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs space-y-1">
              <div>
                <span className="font-bold text-gray-500">Subject: </span>
                <span className="font-semibold text-gray-900">{previewDrawer.version?.subject}</span>
              </div>
              <div>
                <span className="font-bold text-gray-500">Preview: </span>
                <span className="text-gray-600 italic">
                  {previewDrawer.version?.previewText || previewDrawer.version?.preview || '—'}
                </span>
              </div>
            </div>

            {/* Render Container */}
            <div className="flex-1 bg-gray-100 p-4 overflow-y-auto flex justify-center items-start">
              {previewDrawer.loading ? (
                <div className="text-sm text-gray-400 py-12">Rendering template...</div>
              ) : (
                <div
                  className={`bg-white shadow-md rounded-lg overflow-hidden transition-all ${
                    previewDrawer.device === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]'
                  }`}
                  dangerouslySetInnerHTML={{ __html: previewDrawer.html }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Direct Copy Editor Modal */}
      {editModal.isOpen && editModal.version && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-scale-up border border-gray-200">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-gray-900 to-indigo-950 text-white flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-bold text-base">Edit Version Copy</h3>
                  <span className="px-2 py-0.5 rounded text-xs font-mono bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                    {editModal.version.versionId}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-white/10 text-gray-200">
                    {editModal.version.campaignId}
                  </span>
                </div>
                <p className="text-xs text-gray-300 mt-0.5">
                  Directly adjust subject lines, body copy, and loan disclaimers before approval.
                </p>
              </div>
              <button
                onClick={() => setEditModal(prev => ({ ...prev, isOpen: false, version: null }))}
                className="text-gray-400 hover:text-white p-1 text-lg rounded-lg hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1 text-xs">
              {/* Compliance Helper Action */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-blue-900">
                  <span className="text-base">💡</span>
                  <div>
                    <div className="font-bold">Norwegian Compliance Quick-Fix</div>
                    <div className="text-[11px] text-blue-700">
                      Auto-replace "banker" / "bankene" with "långivere" / "långiverne" across subject and body.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleQuickReplaceTerms}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm text-xs whitespace-nowrap"
                >
                  🔄 Replace "banker" → "långivere"
                </button>
              </div>

              {/* Subject Line Input */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-gray-700 font-bold">
                  <label>Subject Line</label>
                  <span className="text-[11px] text-gray-400 font-normal font-mono">
                    {editModal.subject.length} chars
                  </span>
                </div>
                <input
                  type="text"
                  value={editModal.subject}
                  onChange={e => setEditModal(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full text-xs border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none font-medium"
                  placeholder="Subject line..."
                />
              </div>

              {/* Preview Text Input */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-gray-700 font-bold">
                  <label>Preview Text (Preheader)</label>
                  <span className="text-[11px] text-gray-400 font-normal font-mono">
                    {editModal.previewText.length} chars
                  </span>
                </div>
                <input
                  type="text"
                  value={editModal.previewText}
                  onChange={e => setEditModal(prev => ({ ...prev, previewText: e.target.value }))}
                  className="w-full text-xs border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  placeholder="Preview text shown in email client inbox..."
                />
              </div>

              {/* Email Body */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-gray-700 font-bold">
                  <label>Email Body Text</label>
                  <span className="text-[11px] text-gray-400 font-normal">
                    Plain text / copy layout
                  </span>
                </div>
                <textarea
                  rows={8}
                  value={editModal.textContent}
                  onChange={e => setEditModal(prev => ({ ...prev, textContent: e.target.value }))}
                  className="w-full text-xs border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-400 focus:outline-none font-sans leading-relaxed"
                  placeholder="Enter email body text..."
                />
              </div>

              {/* Legal Representative Example / Disclaimer */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-gray-700 font-bold">
                  <label>Legal Representative Example (Finanstilsynet / Forbrukertilsynet Disclaimer)</label>
                  <span className="text-[11px] text-amber-600 font-medium">
                    Mandatory for Credit Offers
                  </span>
                </div>
                <textarea
                  rows={3}
                  value={editModal.disclaimer}
                  onChange={e => setEditModal(prev => ({ ...prev, disclaimer: e.target.value }))}
                  className="w-full text-xs border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-[11px] bg-gray-50"
                  placeholder="e.g. Renteeksempel: 150 000 kr o/5 år, eff. rente 13,2%..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setEditModal(prev => ({ ...prev, isOpen: false, version: null }))}
                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSaveEdit(false)}
                  disabled={editModal.saving}
                  className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-800 text-xs font-bold rounded-lg transition-colors shadow-sm disabled:opacity-50"
                >
                  {editModal.saving ? 'Saving...' : '💾 Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveEdit(true)}
                  disabled={editModal.saving}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <span>✓</span>
                  <span>{editModal.saving ? 'Saving...' : 'Save & Approve'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Audit Modal */}
      {aiAuditModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-lg shadow-inner">
                  🤖
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-white">AI Compliance & Offer Audit</h3>
                    {aiAuditResult && (
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold uppercase tracking-wide ${
                          aiAuditResult.overallStatus === 'pass'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : aiAuditResult.overallStatus === 'warning'
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        }`}
                      >
                        {aiAuditResult.overallStatus === 'pass' ? '✓ Compliant' : aiAuditResult.overallStatus === 'warning' ? '⚠ Warnings' : '✕ Issues Detected'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    {activeCampaign?.advertiser || aiAuditResult?.advertiser || ''} ({activeCampaign?.campaignId || aiAuditResult?.campaignId || ''}) · Market: {selectedGeo} · Category: {activeCampaign?.category || aiAuditResult?.category || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAiAuditModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-slate-50/50">
              {aiAuditLoading ? (
                <div className="py-20 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-14 h-14 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  <div className="space-y-1.5">
                    <h4 className="text-base font-bold text-gray-900">Evaluating Campaign Compliance...</h4>
                    <p className="text-xs text-gray-500 max-w-md">
                      Bedrock Claude Haiku is scraping the active landing page, auditing tag macros, checking regulatory caps & partner phrasing, and screening subject lines.
                    </p>
                  </div>
                </div>
              ) : aiAuditResult ? (
                <>
                  {/* Top Score & Summary Banner */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Score Card */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overall Score</span>
                      <div className="mt-1 text-3xl font-black text-gray-900 flex items-baseline gap-1">
                        <span
                          className={
                            aiAuditResult.overallScore >= 85
                              ? 'text-emerald-600'
                              : aiAuditResult.overallScore >= 70
                              ? 'text-amber-600'
                              : 'text-rose-600'
                          }
                        >
                          {aiAuditResult.overallScore}
                        </span>
                        <span className="text-sm font-bold text-gray-400">/100</span>
                      </div>
                      <span className="text-[11px] text-gray-500 mt-1 font-medium">
                        {aiAuditResult.versionAudits?.length || 0} versions evaluated
                      </span>
                    </div>

                    {/* Landing Page Offer Card */}
                    <div className="md:col-span-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-900">Landing Page Offer Alignment</span>
                            {aiAuditResult.landingPageScraped ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                Scraped & Verified
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                                URL Unreachable / Missing
                              </span>
                            )}
                          </div>
                          {aiAuditResult.landingPageUrl && (
                            <a
                              href={aiAuditResult.landingPageUrl.startsWith('http') ? aiAuditResult.landingPageUrl : `https://${aiAuditResult.landingPageUrl}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] font-semibold text-indigo-600 hover:underline flex items-center gap-1"
                            >
                              <span>Open URL</span>
                              <span>↗</span>
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1.5 leading-relaxed font-medium">
                          {aiAuditResult.landingPageOfferSummary || 'Landing page value propositions screened for alignment.'}
                        </p>
                      </div>
                      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center gap-2 text-xs text-indigo-900 bg-indigo-50/70 px-3 py-1.5 rounded-lg">
                        <span className="font-bold">Executive Summary:</span>
                        <span className="text-indigo-800 line-clamp-1">{aiAuditResult.summary}</span>
                      </div>
                    </div>
                  </div>

                  {/* Versions Navigation Tabs */}
                  <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                    <button
                      onClick={() => setSelectedAuditVersionId('all')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                        selectedAuditVersionId === 'all'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      All Versions Overview ({aiAuditResult.versionAudits?.length || 0})
                    </button>
                    {aiAuditResult.versionAudits?.map(va => {
                      const errCount = va.issues?.filter(i => i.severity === 'error').length || 0;
                      const warnCount = va.issues?.filter(i => i.severity === 'warning').length || 0;
                      return (
                        <button
                          key={va.versionId}
                          onClick={() => setSelectedAuditVersionId(va.versionId)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                            selectedAuditVersionId === va.versionId
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          <span>{va.versionId}</span>
                          <span
                            className={`w-2 h-2 rounded-full ${
                              va.status === 'pass'
                                ? 'bg-emerald-400'
                                : va.status === 'warning'
                                ? 'bg-amber-400'
                                : 'bg-rose-500'
                            }`}
                          />
                          {(errCount > 0 || warnCount > 0) && (
                            <span className="text-[10px] opacity-80">
                              ({errCount > 0 ? `${errCount} err` : `${warnCount} warn`})
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* View: All Versions Overview */}
                  {selectedAuditVersionId === 'all' && (
                    <div className="space-y-4">
                      {/* Grid of 5 Versions */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {aiAuditResult.versionAudits?.map(va => {
                          const matchingVer = versions.find(v => v.versionId === va.versionId);
                          const errs = va.issues?.filter(i => i.severity === 'error').length || 0;
                          const warns = va.issues?.filter(i => i.severity === 'warning').length || 0;
                          return (
                            <div
                              key={va.versionId}
                              className={`bg-white rounded-xl p-3 border shadow-sm flex flex-col justify-between cursor-pointer hover:border-indigo-300 transition-colors ${
                                va.status === 'pass'
                                  ? 'border-emerald-200'
                                  : va.status === 'warning'
                                  ? 'border-amber-200'
                                  : 'border-rose-300'
                              }`}
                              onClick={() => setSelectedAuditVersionId(va.versionId)}
                            >
                              <div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-extrabold text-gray-900">{va.versionId}</span>
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold ${
                                      va.status === 'pass'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : va.status === 'warning'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-rose-100 text-rose-800'
                                    }`}
                                  >
                                    {va.score}/100
                                  </span>
                                </div>
                                <div className="mt-2 space-y-1 text-[11px] text-gray-600">
                                  <div className="flex items-center justify-between">
                                    <span>🏷️ Tags:</span>
                                    <span>{va.checks?.tags?.pass ? '✓' : '✕'}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span>🎯 Landing Page:</span>
                                    <span>{va.checks?.landingPageAlignment?.pass ? '✓' : '✕'}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span>⚖️ Compliance:</span>
                                    <span>{va.checks?.complianceRules?.pass ? '✓' : '✕'}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span>📧 Subject:</span>
                                    <span>{va.checks?.subjectLineHygiene?.pass ? '✓' : '✕'}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span>📜 Disclaimer:</span>
                                    <span>{va.checks?.disclaimer?.pass ? '✓' : '✕'}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[11px]">
                                <span className="text-gray-500 font-medium">
                                  {errs > 0 ? `${errs} error(s)` : warns > 0 ? `${warns} warning(s)` : 'Clean'}
                                </span>
                                {matchingVer && (
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setAiAuditModalOpen(false);
                                      handleOpenEdit(matchingVer);
                                    }}
                                    className="text-indigo-600 font-bold hover:underline"
                                  >
                                    Edit →
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Unified Issues List Across All Versions */}
                      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
                        <h4 className="text-xs font-bold text-gray-900 flex items-center gap-2">
                          <span>Actionable Issues & Recommendations Across Campaign</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">
                            {aiAuditResult.versionAudits?.flatMap(va => va.issues || []).length || 0} items
                          </span>
                        </h4>

                        {aiAuditResult.versionAudits?.flatMap(va => va.issues || []).length === 0 ? (
                          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
                            <span>✓</span>
                            <span>No compliance violations, broken tags, or offer mismatches found. This campaign is in prime condition!</span>
                          </div>
                        ) : (
                          <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                            {aiAuditResult.versionAudits?.map(va => {
                              const matchingVer = versions.find(v => v.versionId === va.versionId);
                              return va.issues?.map((iss, idx) => (
                                <div
                                  key={`${va.versionId}-${idx}`}
                                  className={`p-3 rounded-lg border text-xs space-y-1.5 ${
                                    iss.severity === 'error'
                                      ? 'bg-rose-50/50 border-rose-200 text-rose-900'
                                      : 'bg-amber-50/50 border-amber-200 text-amber-900'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-extrabold px-1.5 py-0.5 rounded bg-white/80 border border-gray-200 text-gray-800">
                                        {va.versionId}
                                      </span>
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase ${
                                          iss.severity === 'error'
                                            ? 'bg-rose-200 text-rose-800'
                                            : 'bg-amber-200 text-amber-800'
                                        }`}
                                      >
                                        {iss.severity}
                                      </span>
                                      <span className="font-bold">{iss.message}</span>
                                    </div>
                                    {matchingVer && (
                                      <button
                                        onClick={() => {
                                          setAiAuditModalOpen(false);
                                          handleOpenEdit(matchingVer);
                                        }}
                                        className="px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold text-gray-700 text-[11px] shadow-sm flex-shrink-0"
                                      >
                                        Edit {va.versionId}
                                      </button>
                                    )}
                                  </div>
                                  {iss.suggestion && (
                                    <div className="text-[11px] text-gray-700 bg-white/70 p-2 rounded border border-gray-200/60 font-medium">
                                      <strong className="text-indigo-600">Fix Recommendation:</strong> {iss.suggestion}
                                    </div>
                                  )}
                                </div>
                              ));
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* View: Individual Version Details */}
                  {selectedAuditVersionId !== 'all' && (
                    <div className="space-y-4">
                      {(() => {
                        const va = aiAuditResult.versionAudits?.find(v => v.versionId === selectedAuditVersionId);
                        const matchingVer = versions.find(v => v.versionId === selectedAuditVersionId);
                        if (!va) return <div>Version not found</div>;

                        return (
                          <>
                            {/* Version Header & Score */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-base font-extrabold text-gray-900">{va.versionId}</span>
                                <span className="text-xs text-gray-500 font-medium">
                                  Subject: "{matchingVer?.subject || 'N/A'}"
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <span className="text-xs text-gray-400 font-medium block">Audit Score</span>
                                  <span
                                    className={`text-lg font-black ${
                                      va.score >= 85
                                        ? 'text-emerald-600'
                                        : va.score >= 70
                                        ? 'text-amber-600'
                                        : 'text-rose-600'
                                    }`}
                                  >
                                    {va.score}/100
                                  </span>
                                </div>
                                {matchingVer && (
                                  <button
                                    onClick={() => {
                                      setAiAuditModalOpen(false);
                                      handleOpenEdit(matchingVer);
                                    }}
                                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-sm"
                                  >
                                    ✏️ Edit Copy
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* 5 Checklist Items Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold mb-1">
                                  <span>🏷️ Tags</span>
                                  <span className={va.checks?.tags?.pass ? 'text-emerald-600' : 'text-rose-600'}>
                                    {va.checks?.tags?.pass ? '✓ PASS' : '✕ FAIL'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3">
                                  {va.checks?.tags?.details || 'Tags checked'}
                                </p>
                              </div>

                              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold mb-1">
                                  <span>🎯 Alignment</span>
                                  <span className={va.checks?.landingPageAlignment?.pass ? 'text-emerald-600' : 'text-rose-600'}>
                                    {va.checks?.landingPageAlignment?.pass ? '✓ PASS' : '✕ FAIL'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3">
                                  {va.checks?.landingPageAlignment?.details || 'Landing page alignment'}
                                </p>
                              </div>

                              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold mb-1">
                                  <span>⚖️ Compliance</span>
                                  <span className={va.checks?.complianceRules?.pass ? 'text-emerald-600' : 'text-rose-600'}>
                                    {va.checks?.complianceRules?.pass ? '✓ PASS' : '✕ FAIL'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3">
                                  {va.checks?.complianceRules?.details || 'Statutory rules verified'}
                                </p>
                              </div>

                              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold mb-1">
                                  <span>📧 Subject</span>
                                  <span className={va.checks?.subjectLineHygiene?.pass ? 'text-emerald-600' : 'text-rose-600'}>
                                    {va.checks?.subjectLineHygiene?.pass ? '✓ PASS' : '✕ FAIL'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3">
                                  {va.checks?.subjectLineHygiene?.details || '0 brand & 0 BankID in subject'}
                                </p>
                              </div>

                              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between text-xs font-bold mb-1">
                                  <span>📜 Disclaimer</span>
                                  <span className={va.checks?.disclaimer?.pass ? 'text-emerald-600' : 'text-rose-600'}>
                                    {va.checks?.disclaimer?.pass ? '✓ PASS' : '✕ FAIL'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3">
                                  {va.checks?.disclaimer?.details || 'Statutory representative example'}
                                </p>
                              </div>
                            </div>

                            {/* Issues for this Version */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                              <h4 className="text-xs font-bold text-gray-900">
                                Issues for {va.versionId} ({va.issues?.length || 0})
                              </h4>
                              {va.issues?.length === 0 ? (
                                <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 text-xs font-semibold flex items-center gap-2">
                                  <span>✓</span>
                                  <span>No compliance or tag issues detected for this version.</span>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {va.issues?.map((iss, idx) => (
                                    <div
                                      key={idx}
                                      className={`p-3 rounded-lg border text-xs space-y-1 ${
                                        iss.severity === 'error'
                                          ? 'bg-rose-50/50 border-rose-200 text-rose-900'
                                          : 'bg-amber-50/50 border-amber-200 text-amber-900'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2 font-bold">
                                        <span
                                          className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase ${
                                            iss.severity === 'error'
                                              ? 'bg-rose-200 text-rose-800'
                                              : 'bg-amber-200 text-amber-800'
                                          }`}
                                        >
                                          {iss.severity}
                                        </span>
                                        <span>{iss.message}</span>
                                      </div>
                                      {iss.suggestion && (
                                        <div className="text-[11px] text-gray-700 bg-white/80 p-2 rounded border border-gray-200 mt-1 font-medium">
                                          <strong className="text-indigo-600">Fix Recommendation:</strong> {iss.suggestion}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleAiAudit}
                disabled={aiAuditLoading}
                className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <span>🔄</span>
                <span>Re-run Audit</span>
              </button>
              <div className="flex items-center gap-2">
                {aiAuditResult && aiAuditResult.overallStatus === 'pass' && (
                  <button
                    type="button"
                    onClick={() => {
                      setAiAuditModalOpen(false);
                      handleApprove({ approveAll: true });
                    }}
                    disabled={actionLoading || !!activeCampaign?.isFullyApproved}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    <span>✓</span>
                    <span>Approve Entire Campaign (All {activeCampaign?.totalVersions || aiAuditResult.versionAudits?.length || 5})</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setAiAuditModalOpen(false)}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side-by-Side Base vs Variant Comparison Modal */}
      {compareBaseModal.isOpen && compareBaseModal.variant && compareBaseModal.base && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-purple-950 via-slate-900 to-indigo-950 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-lg shadow-inner">
                  ⚖️
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold tracking-tight">
                      Side-by-Side Copy Audit: Baseline vs {compareBaseModal.variant.targetEsp || 'Moosend'} Variant
                    </h3>
                    <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-purple-500/30 text-purple-200 border border-purple-400/30 uppercase">
                      {compareBaseModal.variant.versionId} vs {compareBaseModal.base.versionId}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    {activeCampaign?.advertiser || activeCampaign?.name} ({activeCampaign?.campaignId}) · Market: {selectedGeo} · Compare phrasing variations while verifying invariant legal disclaimers and macros
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCompareBaseModal({ isOpen: false, variant: null, base: null })}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Invariant Health Checklist Bar */}
            <div className="px-6 py-2.5 bg-slate-100 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-4">
                {/* Check 1: Dynamic Link Macro */}
                {(() => {
                  const baseHasMacro = (compareBaseModal.base?.textContent || '').includes('{campaignLink}') || (compareBaseModal.base?.htmlContent || '').includes('{campaignLink}');
                  const variantHasMacro = (compareBaseModal.variant?.textContent || '').includes('{campaignLink}') || (compareBaseModal.variant?.htmlContent || '').includes('{campaignLink}');
                  const macroOk = (!baseHasMacro && !variantHasMacro) || (baseHasMacro === variantHasMacro);
                  return (
                    <div className="flex items-center gap-1.5 font-semibold">
                      <span className={macroOk ? 'text-emerald-600' : 'text-amber-600'}>
                        {macroOk ? '✓' : '⚠️'}
                      </span>
                      <span className="text-slate-700">Dynamic Link Macro:</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${macroOk ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {macroOk ? 'Preserved' : 'Difference Detected'}
                      </span>
                    </div>
                  );
                })()}

                {/* Check 2: Disclaimer Identical */}
                {(() => {
                  const baseDisc = (compareBaseModal.base?.disclaimer || '').trim();
                  const varDisc = (compareBaseModal.variant?.disclaimer || '').trim();
                  const discIdentical = baseDisc === varDisc;
                  return (
                    <div className="flex items-center gap-1.5 font-semibold">
                      <span className={discIdentical ? 'text-emerald-600' : 'text-amber-600'}>
                        {discIdentical ? '✓' : '⚠️'}
                      </span>
                      <span className="text-slate-700">Statutory Disclaimer:</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${discIdentical ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {discIdentical ? 'Byte-Identical' : 'Modified'}
                      </span>
                    </div>
                  );
                })()}

                {/* Check 3: Word variation */}
                {(() => {
                  const baseWords = (compareBaseModal.base?.textContent || '').split(/\s+/).filter(Boolean).length;
                  const varWords = (compareBaseModal.variant?.textContent || '').split(/\s+/).filter(Boolean).length;
                  return (
                    <div className="flex items-center gap-1.5 text-slate-500 text-[11px]">
                      <span>Words:</span>
                      <span className="font-mono font-bold text-slate-700">{baseWords} base vs {varWords} variant</span>
                    </div>
                  );
                })()}
              </div>

              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                  compareBaseModal.variant?.approvalStatus === 'approved' || compareBaseModal.variant?.isApproved
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-amber-100 text-amber-800 border border-amber-300'
                }`}>
                  Variant Status: {compareBaseModal.variant?.approvalStatus || (compareBaseModal.variant?.isApproved ? 'approved' : 'draft')}
                </span>
              </div>
            </div>

            {/* Side-by-Side Comparison Columns */}
            <div className="p-6 overflow-y-auto flex-1 bg-slate-50 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LEFT COLUMN: FeedBlitz Base */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                <div className="px-4 py-3 bg-slate-100/80 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded font-bold text-xs">
                      ⚡ FeedBlitz Base ({compareBaseModal.base.versionId})
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500">Golden Copy</span>
                  </div>
                  <span className="text-xs text-emerald-700 font-bold flex items-center gap-1">
                    ✓ Approved
                  </span>
                </div>

                <div className="p-4 space-y-4 flex-1 overflow-y-auto">
                  {/* Subject Line */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Primary Subject Line
                    </label>
                    <div className="text-sm font-semibold text-slate-900 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                      {compareBaseModal.base.subject || '—'}
                    </div>
                  </div>

                  {/* Preview Text */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Preview / Preheader Text
                    </label>
                    <div className="text-xs text-slate-700 bg-slate-50 p-2 rounded-lg border border-slate-200 italic">
                      {compareBaseModal.base.previewText || compareBaseModal.base.preview || '—'}
                    </div>
                  </div>

                  {/* Rotating Subject Pairs */}
                  {Array.isArray(compareBaseModal.base.subjectPairs) && compareBaseModal.base.subjectPairs.length > 0 && (
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                        Rotating Subject Pairs ({compareBaseModal.base.subjectPairs.length})
                      </label>
                      <div className="space-y-1.5">
                        {compareBaseModal.base.subjectPairs.map((p, idx) => (
                          <div key={idx} className="text-xs bg-slate-50 p-2 rounded border border-slate-200">
                            <span className="font-semibold text-slate-800">#{idx + 1}: {p.subject}</span>
                            {p.previewText && <p className="text-[11px] text-slate-500 italic mt-0.5">{p.previewText}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Body Text */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Body Copy (Text Content)
                    </label>
                    <pre className="text-xs font-sans text-slate-800 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border border-slate-200 leading-relaxed font-normal max-h-96 overflow-y-auto">
                      {compareBaseModal.base.textContent || compareBaseModal.base.body || 'No text content'}
                    </pre>
                  </div>

                  {/* Disclaimer */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Statutory Disclaimer
                    </label>
                    <div className="text-[11px] text-slate-500 font-mono bg-slate-100 p-2.5 rounded-lg border border-slate-200 leading-normal">
                      {compareBaseModal.base.disclaimer || 'No disclaimer specified'}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Moosend / ESP Variant */}
              <div className="bg-white rounded-xl border border-purple-200 shadow-sm flex flex-col overflow-hidden ring-1 ring-purple-100">
                <div className="px-4 py-3 bg-purple-50/70 border-b border-purple-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-purple-600 text-white rounded font-bold text-xs">
                      📬 {compareBaseModal.variant.targetEsp ? compareBaseModal.variant.targetEsp.toUpperCase() : 'MOOSEND'} Variant ({compareBaseModal.variant.versionId})
                    </span>
                    <span className="text-[11px] font-semibold text-purple-700">Multi-ESP Edition</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenPreview(compareBaseModal.variant!)}
                      className="px-2 py-1 bg-white hover:bg-purple-100 text-purple-700 text-xs font-bold rounded border border-purple-300 transition-colors flex items-center gap-1 shadow-2xs"
                      title="Open in live HTML template drawer"
                    >
                      <span>👁️</span>
                      <span>Preview HTML</span>
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-4 flex-1 overflow-y-auto">
                  {/* Subject Line */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-purple-600 block mb-1">
                      Primary Subject Line (Varied)
                    </label>
                    <div className="text-sm font-semibold text-purple-950 bg-purple-50/50 p-2.5 rounded-lg border border-purple-200">
                      {compareBaseModal.variant.subject || '—'}
                    </div>
                  </div>

                  {/* Preview Text */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-purple-600 block mb-1">
                      Preview / Preheader Text (Varied)
                    </label>
                    <div className="text-xs text-purple-900 bg-purple-50/50 p-2 rounded-lg border border-purple-200 italic">
                      {compareBaseModal.variant.previewText || compareBaseModal.variant.preview || '—'}
                    </div>
                  </div>

                  {/* Rotating Subject Pairs */}
                  {Array.isArray(compareBaseModal.variant.subjectPairs) && compareBaseModal.variant.subjectPairs.length > 0 && (
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-purple-600 block mb-1">
                        Rotating Subject Pairs ({compareBaseModal.variant.subjectPairs.length})
                      </label>
                      <div className="space-y-1.5">
                        {compareBaseModal.variant.subjectPairs.map((p, idx) => (
                          <div key={idx} className="text-xs bg-purple-50/40 p-2 rounded border border-purple-200">
                            <span className="font-semibold text-purple-950">#{idx + 1}: {p.subject}</span>
                            {p.previewText && <p className="text-[11px] text-purple-800 italic mt-0.5">{p.previewText}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Body Text */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-purple-600 block mb-1">
                      Body Copy (Varied Phrasing)
                    </label>
                    <pre className="text-xs font-sans text-slate-800 whitespace-pre-wrap bg-purple-50/30 p-3 rounded-lg border border-purple-200 leading-relaxed font-normal max-h-96 overflow-y-auto">
                      {compareBaseModal.variant.textContent || compareBaseModal.variant.body || 'No text content'}
                    </pre>
                  </div>

                  {/* Disclaimer */}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-purple-600 block mb-1">
                      Statutory Disclaimer (Locked)
                    </label>
                    <div className="text-[11px] text-slate-600 font-mono bg-slate-100 p-2.5 rounded-lg border border-purple-200 leading-normal">
                      {compareBaseModal.variant.disclaimer || 'No disclaimer specified'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500 flex items-center gap-1.5">
                <span>🛡️</span>
                <span>All invariant legal numbers, caps, and disclaimers strictly enforced across ESPs.</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const v = compareBaseModal.variant;
                    setCompareBaseModal({ isOpen: false, variant: null, base: null });
                    if (v) handleOpenEdit(v);
                  }}
                  className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <span>✏️</span>
                  <span>Edit Variant</span>
                </button>

                {compareBaseModal.variant.approvalStatus !== 'approved' && !compareBaseModal.variant.isApproved && (
                  <button
                    type="button"
                    onClick={async () => {
                      const v = compareBaseModal.variant;
                      setCompareBaseModal({ isOpen: false, variant: null, base: null });
                      if (v) await handleApprove({ versionId: v.versionId });
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
                  >
                    <span>✓</span>
                    <span>Approve Variant</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setCompareBaseModal({ isOpen: false, variant: null, base: null })}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
